<template>
    <div 
        class="details-show-info__container"
        :class="{'details-show-info__container-live-sport': hasLiveSportLabel}"
    >
        <img v-if="previewImageLoading" alt="" class="details-show-info__image" :src="loaderImgSrc" loading="lazy">
        <template v-else>
            <img v-if="previewImage.src" alt="" class="details-show-info__image" :src="previewImage.src" loading="lazy">
            <common-icon v-else class="details-show-info__image" :icon-name="'IconNoPicture'" />
        </template>
        <div class="details-show-info">
            <div class="details-show-info__side details-show-info__side_left">
                <div class="details-show-info__title">
                    <div class="details-show-info__title-content">
                        {{ title }}
                    </div>
                </div>
                <div v-if="hasLiveSportLabel" class="details-show-info__live-sport-event">
                    <span class="details-show-info__live-sport-event-label">{{liveSportLabel}}</span>
                    <span class="details-show-info__live-sport-event-text">{{liveSportText}}</span>
                </div>
                <div class="details-show-info__second-line">
                    <span v-if="genres" class="details-show-info__genres">{{ genres }}</span>
                    <span v-if="durationText" class="details-show-info__duration">{{ durationText }}</span>
                    <program-labels
                        :advisory-text="programInfo.advisoryText"
                        :high-definition="programInfo.highDefinition"
                        :closed-captions="programInfo.closedCaptions"
                        :descriptive-audio="programInfo.descriptiveAudio"
                        :is-restart-available="programInfo.isRestartAvailable"
                    />
                </div>
                <div class="details-show-info__description">
                    {{ programInfo.description }}
                </div>
                <div class="details-info-controls">
                    <div v-if="isPlayableItem" class="details-info-controls__holder">
                        <button 
                            :id="btns.play"
                            ref="btnPlay"
                            :data-keyright="getRightBtnId(btns.play)"
                            class="details-info-controls__button"
                            @click="playProgram"
                            @focusin="onFocusIn"
                            @focusout="onFocusOut"
                        >
                            <loader v-if="isPlayStatusChanging" class="details-info-controls__loader loader_default-color" />
                            <span v-else class="details-info-controls__icon">
                                <common-icon :icon-name="'IconPlay'" />
                            </span>
                        </button>
                        <span v-if="!isStreamPosition" class="details-info-controls__text">{{ btnPlayText }}</span>
                        <span v-else class="details-info-controls__text">{{ btnResumeText }}</span>
                    </div>
                    <div v-if="isAvailableGoToChannel" class="details-info-controls__holder">
                        <button 
                            :id="btns.goToChannel"
                            ref="btnGoToChannel"
                            :data-keyright="getRightBtnId(btns.goToChannel)"
                            class="details-info-controls__button"
                            @click="playLiveProgramOfChannel"
                            @focusin="onFocusIn"
                            @focusout="onFocusOut"
                        >
                            <loader v-if="isPlayStatusChanging" class="details-info-controls__loader loader_default-color" />
                            <span v-else class="details-info-controls__icon">
                                <common-icon :icon-name="'IconTv'" />
                            </span>
                        </button>
                        <span class="details-info-controls__text">{{ btnGoToChannelText }}</span>
                    </div>
                    <div v-if="isAvailableWatchLive" class="details-info-controls__holder">
                        <button 
                            :id="btns.watchLive"
                            ref="btnWatchLive"
                            :data-keyright="getRightBtnId(btns.watchLive)"
                            class="details-info-controls__button"
                            @click="playLiveProgramOfChannel"
                            @focusin="onFocusIn"
                            @focusout="onFocusOut"
                        >
                            <loader v-if="isPlayStatusChanging" class="details-info-controls__loader loader_default-color" />
                            <span v-else class="details-info-controls__icon">
                                <common-icon :icon-name="'IconPlay'" />
                            </span>
                        </button>
                        <span class="details-info-controls__text">{{ btnWatchLiveText }}</span>
                    </div>
                    <div class="details-info-controls__holder">
                        <button
                            :id="btns.add"
                            ref="btnAdd"
                            :data-keyleft="getLeftBtnId(btns.add)"
                            :data-keyright="getRightBtnId(btns.add)"
                            class="details-info-controls__button"
                            @click="toggleFavorite"
                            @focusin="onFocusIn"
                            @focusout="onFocusOut"
                        >
                            <loader v-if="isFavsStatusChanging || isLoadingFavoriteStatus" class="details-info-controls__loader loader_default-color" />
                            <span v-else class="details-info-controls__icon details-info-controls__icon_add">
                                <common-icon v-if="isFavorite" :icon-name="'IconCheck'" />
                                <common-icon v-else :icon-name="'IconAdd'" />
                            </span>
                        </button>
                        <span class="details-info-controls__text">
                            {{ favoriteTxt }}
                        </span>
                    </div>
                    <div v-if="isChannels" class="details-info-controls__holder">
                        <button
                            :id="btns.select"
                            ref="btnSelect"
                            :data-keyleft="getLeftBtnId(btns.select)"
                            :data-keyright="getRightBtnId(btns.select)"
                            class="details-info-controls__button details-info-controls__button-padding"
                            @click="showModalProgram"
                            @focusin="onFocusIn"
                            @focusout="onFocusOut"
                        >
                            <img
                                class="details-info-controls__image"
                                alt=""
                                :src="urlImage"
                                loading="lazy"
                                :class="{'details-info-controls__image_disabled': isDisabledChannels}"
                            >
                        </button>
                        <span class="details-info-controls__text">
                            {{ captionBtnChannel }}
                        </span>
                    </div>
                    <div v-if="isSeries" class="details-info-controls__holder">
                        <button
                            :id="btns.select"
                            ref="btnSelect"
                            :data-keyleft="getLeftBtnId(btns.select)"
                            :data-keyright="getRightBtnId(btns.select)"
                            class="details-info-controls__button details-info-controls__button-padding"
                            @click="showModalSeasons"
                            @focusin="onFocusIn"
                            @focusout="onFocusOut"
                        >
                            <span
                                class="details-info-controls__name"
                                :class="{'color_disabled': isDisabledSeasons}"
                            >
                                Season {{ seasonNumber }}
                            </span>
                        </button>
                        <span class="details-info-controls__text">
                            {{ captionBtnSeason }}
                        </span>
                    </div>
                    <div v-if="isBtnTrailerVisible" class="details-info-controls__holder">
                        <button
                            :id="btns.playTrailer"
                            :data-keyleft="getLeftBtnId(btns.playTrailer)"
                            :data-keyright="getRightBtnId(btns.playTrailer)"
                            class="details-info-controls__button"
                            @click="showTrailer"
                            @focusin="onFocusIn"
                            @focusout="onFocusOut"
                        >
                            <span class="details-info-controls__icon">
                                <common-icon :icon-name="'IconTrailer'" />
                            </span>
                        </button>
                        <span class="details-info-controls__text">{{ captionBtnTrailer }}</span>
                    </div>
                </div>
                <hr class="details-show-info__delimiter">
            </div>
            <div class="details-show-info__side details-show-info__side_right">
                <div class="details-show-info__image-overlay" />
            </div>
            <div class="details-bottom" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { type PropType, ref, type Ref, computed, watch, getCurrentInstance } from "vue";
    import { type ContentProperties, PlayItem, type Channel, ContentType, type ProgramGuideItem } from "@ca.vmedia.tv/domains";
    import { ContentItemProps, ProtoItem } from "@ca.vmedia.tv/domains";
    import { Strings} from "@/locales/types/enums/strings";
    import { AppConstants} from "@/common";
    import { ImgUrlUtils, TimeUtils, Logger } from '@/utils';
    import { useApplicationStore } from "@/stores/Application";
    import type { Advisory } from "@ca.vmedia.tv/swagger-api";
    import { useOnline, useImage } from '@vueuse/core';
    import { useI18n } from "vue-i18n";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import type { ExposeInterface } from "./types";
    import { storeToRefs } from 'pinia';
    import { useMedia } from "@/stores/Media";
    import { GetItemPropertiesRequest } from "@/stores/Media/types";
    import { useUserInfoStore } from "@/stores/UserInfo";
    import { useParentControlStore } from "@/stores/ParentControl";
    import { useFavorite } from "@/composables/useFavorite";
    import { usePlayerStore } from "@/stores/Player";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import { useDetailShowPageStore } from "@/stores/DetailShowPage";
    import type { AxiosError } from "axios";
    import { ChannelLiveCardData, type SeasonsCardData } from "@/components/Cards/models";
    import { useProgramInfo } from "@/composables/useProgramInfo/useProgramInfo";
    import { LangCodes } from "@/common/types/languages";

    const ApplicationStore = useApplicationStore();
    const {
        getIsPreviewSingInModal: isPreviewSignInModal,
    } = storeToRefs(ApplicationStore);
    const { 
        addSelectedIdProgram, 
        showPreviewSignInModal,
        setTooltipeShowing,
        setTooltipeMessage
     } = ApplicationStore;

    const {
        startPlayingProgram,
        startPlayingChannel,
    } = usePlayerStore();

    const { 
        deleteFromFavorites, 
        addToFavorites, 
        hasDeletedIdsFavorite,
        getFavStatusChanging: isFavsStatusChanging 
    } = useFavorite();

    const DetailPage = useDetailPageStore();
    const {
        getSeasonModalVisible: isSeasonModalVisible,
        getProgramModalVisible: isProgramModalVisible,
        getPlayStatusChanging: isPlayStatusChanging,
        getConvertedGenres
    } = storeToRefs(DetailPage);
    const { showSeasonModal, hideSeasonModal, hideProgramModal, showProgramModal, convertGenres, setPlayStatusChanging } = DetailPage;

    const DetailShowPage = useDetailShowPageStore();
    const {
        getCurrentSeason: currentSeason
    } = storeToRefs(DetailShowPage);

    const MediaStoreModule = useMedia();
    const {
        fetchItemProperties,
    } = MediaStoreModule;
    const {
        getStreamPosition: streamPosition,
    } = storeToRefs(MediaStoreModule);


    const UserInfoStoreModule = useUserInfoStore();
    const {
        isGuest,
    } = storeToRefs(UserInfoStoreModule);


    const ParentControlStoreModule = useParentControlStore();
    const {
        SetIncorrectAdultPin,
        ShowParentModal,
        SetTargetWidgetAdultControl
    } = ParentControlStoreModule;
    const {
        getIsModalParentVisible: isTypeModeEnabledPin,
        getIsAdultPinCorrect: isAdultPinCorrect,
        getTargetWidgetAdultControl: targetWidgetAdultControl
    } = storeToRefs(ParentControlStoreModule);


    interface IBtns {
        play: string;
        add: string;
        select: string;
        playTrailer: string;
        goToChannel: string;
        watchLive: string;
    }

    const props = defineProps({
        item: {
            type: [Object, null] as PropType<PlayItem | null>,
            required: true,
        },
        currentChannel: {
            type: [Object, null] as PropType<Channel | null>,
            required: false,
            default: null,
        },
        isPlayable: {
            type: Boolean,
            default: false,
        },
        legacyPlay: {
            type: Boolean,
            default: false,
        },
        channels: {
            type: [Array, null] as PropType<Channel[] | null>,
            default: () => [],
        },
        seasons: {
            type: [Array, null] as PropType<SeasonsCardData[] | null>,
            default: () => [],
        },
        design: {
            type: String,
            default: "",
        }
    });
    
    const btns: IBtns = {
        play: "btnPlay",
        add: "btnAdd",
        select: "btnSelect",
        playTrailer: "btnPlayTrailer",
        goToChannel: "goToChannel",
        watchLive: "watchLive",
    };
    const { isTimeFormat12 } = useAppConfig();
    const internalInstance = getCurrentInstance();
    const emit = defineEmits(['show-trailer', 'play', 'unset-focused']);
    const previewImage: Ref = ref({ src: "" });
    const { isLoading: previewImageLoading } = useImage(previewImage);
    const loaderImgSrc = AppConstants.loadingPreviewUrl;
    const { $domains, $navigator, $services } = useNuxtApp();
    const { t, locale } = useI18n();
    const captionBtnTrailer: string = t(Strings.DETAILS_BTNS_TRAILER).toString();
    const captionBtnChannel: string = t(Strings.DETAILS_BTNS_CHANNEL).toString();
    const captionBtnSeason: string = t(Strings.DETAILS_BTNS_SEASON).toString();
    const addBtnText: string = t(Strings.DETAILS_BTNS_ADD).toString();
    const deleteBtnText: string = t(Strings.DETAILS_BTNS_REMOVE).toString();
    const btnPlayText: string = t(Strings.DETAILS_BTNS_PLAY).toString();
    const btnResumeText: string = t(Strings.DETAILS_BTNS_RESUME).toString();
    const btnGoToChannelText: string = t(Strings.DETAILS_BTNS_GO_TO_CHANNEL).toString();
    const btnWatchLiveText: string = t(Strings.DETAILS_BTNS_WATCH_LIVE).toString();
    const { setCurrentTimes, needsUnlockPin } = useParentControl();
    const isLoadingFavoriteStatus: Ref<boolean> = ref(false);
    const isFavorite: Ref<boolean> = ref(false);
    const btnPlay: Ref<HTMLButtonElement | null> = ref(null);
    const btnAdd: Ref<HTMLButtonElement | null> = ref(null);
    const btnSelect: Ref<HTMLButtonElement | null> = ref(null);
    const btnGoToChannel: Ref<HTMLButtonElement | null> = ref(null);
    const btnWatchLive: Ref<HTMLButtonElement | null> = ref(null);
    const checkedLiveProgram: Ref<ProgramGuideItem | null> = ref(null);
    const programInfo = useProgramInfo(props.item, props.currentChannel, null)

    const hasLiveSportLabel = computed((): boolean => props.item?.IsLiveSportEvent !== null);
    const liveSportLabel = computed((): string => {
        if(PlayItem.isFuture(props.item)) {
            return t(props.item?.IsLiveSportEvent === true ? Strings.DETAILS_LIVE_SPORT_LABEL_UPCOMING : Strings.DETAILS_LIVE_SPORT_LABEL_REBROADCAST);
        }

        if(PlayItem.isLive(props.item)) {
            return t(props.item?.IsLiveSportEvent === true ? Strings.DETAILS_LIVE_SPORT_LABEL_LIVE : Strings.DETAILS_LIVE_SPORT_LABEL_REBROADCAST);
        } 

        return t(Strings.DETAILS_LIVE_SPORT_LABEL_REBROADCAST);
    });
    const liveSportText = computed((): string => {
        if(!props.item?.lengthInS || !props.item.recentAirTime?.startTime) {
            return '';
        }
        const timeFormat = isTimeFormat12 ? 'hh:mm a' : 'HH:mm';
        const dateFormat = locale.value === LangCodes.FR ? 'DD MMM, YYYY' : 'MMM DD, YYYY';
        const date = TimeUtils.getMoment(locale.value, props.item.recentAirTime.startTime).format(dateFormat).toUpperCase();
        const start = TimeUtils.getMoment(locale.value, props.item.recentAirTime.startTime).format(timeFormat).toUpperCase();
        const end = TimeUtils.getMoment(locale.value, props.item.recentAirTime.startTime + props.item.lengthInS * 1000).format(timeFormat).toUpperCase();

        return `(${date}) | ${start} - ${end}`;
    });

    const fetchBackgroundImage = async (): Promise<void> => {
        if (props.item) {
            try {
                const imageUrl = await $domains.imagesRepository.getOnDemandBackgroundImage(props.item.images);
                previewImage.value.src = ImgUrlUtils.src(imageUrl ?? props.item.image, AppConstants.HeaderImageWidth);
            } catch (error) {
                console.error('Error when fetchBackgroundImage:', error)
            }
        }
    };

    const getItemProperties = async(): Promise<void> => {
        if (isGuest.value) {
            isFavorite.value = false;
            return;
        }

        if (!props.item?.id) {
            return;
        }

        isLoadingFavoriteStatus.value = true;
        const properties: ReadonlyArray<ContentProperties> = await fetchItemProperties(
            new GetItemPropertiesRequest([props.item?.id], props.item?.contentType, [ContentItemProps.FavouriteStatee,ContentItemProps.WatchedPosition])
        );

        const isMarkedDeletedIds: boolean = hasDeletedIdsFavorite(`${props.item?.id}:${props.item?.contentType}`);
        isFavorite.value = ((Array.isArray(properties) && properties.length) ? (properties[0].isFavourite ?? false) : false) && !isMarkedDeletedIds;
        isLoadingFavoriteStatus.value = false;
    };
    const getGenres = () => {
        convertGenres(props.item?.contentGenres ?? null);
    }
    const init = (): void => {
        getGenres();
        getItemProperties().catch((error) => {
            Logger.captureError(error as AxiosError | Error);
        });
        void fetchBackgroundImage();
    };

    init();

    const isPlayableItem = computed((): boolean => isGuest.value || props.isPlayable && (isAvailableVcloud.value || isVOD.value));
    const isAvailableWatchLive = computed((): boolean => !isGuest.value && props.item !== null && props.design === "channels" && PlayItem.isLive(props.item));
    const isAvailableGoToChannel = computed((): boolean => !isGuest.value && props.design === "channels" && Boolean(props.currentChannel) && !isPlayableItem.value && !isAvailableWatchLive.value);
    const isAvailableVcloud = computed((): boolean => {
        if (props.item !== null && props.design === "channels" && props.currentChannel) {
            return PlayItem.isVCloud(props.item, props.currentChannel);
        }
        return false;
    });
    const isVOD = computed((): boolean => {
        if (props.item?.contentType) {
            return [ContentType.VOD, ContentType.SVOD, ContentType.CraveTV].includes(props.item.contentType);
        }
        return false;
    });
    const isBtnTrailerVisible = computed((): boolean => props.item && props.item.TrailerVideoId ? true : false);
    const isShowModalVisible = computed((): boolean =>isProgramModalVisible.value || isSeasonModalVisible.value);
    const seasonNumber = computed((): string => {
        if (currentSeason.value) {
            return currentSeason.value.getSeasonNumber();
        }
        return "";
    });
    const durationText = computed((): string | null => {
        if (props.item?.lengthInS){
            const time = props.item.lengthInS;
            const labelHour: string = t(Strings.TIME_SHORTHOUR).toString();
            const labelMinute: string = t(Strings.TIME_SHORTMINUTE).toString();
            const  hour = Math.trunc(time / 3600);
            const [minute] = TimeUtils.getMoment(locale.value, time * 1000).format('mm').split(':');
            const duration: string[] = [];
            
            if (hour !== 0){
                duration.push(`${hour}${labelHour}`);
            } 
            if (Number(minute) !== 0){
                duration.push(`${Number(minute)}${labelMinute}`);
            } 

            if (duration.length === 3) {
                duration.pop();
            }

            return duration.join(' ');
        } else {
            return null;
        }
    });
    const genres = computed(() => getConvertedGenres.value ? getConvertedGenres.value.join(', ') : null);
    const favoriteTxt = computed((): string => isFavorite.value === false ? addBtnText : deleteBtnText);
    const maxDurationForResume = computed((): number => {
        const duration = props.item?.lengthInS || props.item?.length;
        return duration! * 0.9;
    });
    const isStreamPosition = computed((): boolean => {
        if (streamPosition.value > 0 && streamPosition.value < maxDurationForResume.value) {
            return true;
        }
        return false;
    });
    const title = computed((): string => {
        if (props.item && isNotEmpty(props.item.seriesName) && props.design === "series") {
            return props.item.seriesName!;
        }
        if (props.item && isNotEmpty(props.item.name)) {
            return props.item.name!;
        }
        return "";
    });
    const urlImage = computed((): string => props.currentChannel?.image ?? "");

    const isChannels = computed((): boolean => {
        if (props.design === "channels" && props.channels !== null && props.channels.length > 0) {
            return true;
        }
        return false;
    });
    const isSeries = computed((): boolean => {
        if (props.design === "series" && (props.seasons && props.seasons.length > 0)) {
            return true;
        }
        return false;
    });
    const isDisabledChannels = computed((): boolean => {
        if (props.channels !== null && props.channels.length === 1) {
            return true;
        }
        return false;
    });
    const isDisabledSeasons = computed((): boolean => {
        if (props.seasons !== null && props.seasons.length === 1) {
            return true;
        }
        return false;
    });
    const isOffline = computed((): boolean => useOnline().value);

    const getLeftBtnId = (btnId: string): string | null => {
        switch (btnId) {
            case btns.add:
                if (isAvailableWatchLive.value) {
                    return btns.watchLive;
                } else if (isAvailableGoToChannel.value) {
                    return btns.goToChannel;
                } else if (isPlayableItem.value) {
                    return btns.play;
                }

            case btns.select:
                return btns.add;

            case btns.playTrailer:
                if (props.channels !== null && props.channels.length > 1 || (props.seasons && props.seasons.length > 1)) {
                    return btns.select;
                } else {
                    return btns.add;
                }   

            default:
                return null;
        }
    };
    const getRightBtnId = (btnId: string): string | null => {
        switch (btnId) {
            case btns.play:
                return btns.add;

            case btns.goToChannel:
                return btns.add;

            case btns.watchLive:
                return btns.add;

            case btns.add:
                if (props.channels !== null && props.channels.length > 1 || (props.seasons && props.seasons.length > 1)) {
                    return btns.select;
                } else {
                    return btns.playTrailer;
                }

            case btns.select:
                return btns.playTrailer;

            case btns.playTrailer:
                return null;

            default:
                return null;
        }
    };
    const showTrailer = (): void => {
        emit("show-trailer", props.item?.TrailerVideoId);
    };
    const goToTop = (): void => {
        window.scrollTo(0, 0);
    };
    const onFocusIn = (): void => {
        goToTop();
        registerKeyDownListeners();
    };
    const onFocusOut = (): void => {
        unregisterKeyDownListeners();
    };
    const focus = (): void => {
        if (isPlayableItem.value && btnPlay.value) {
            btnPlay.value.focus();
        } else if (isAvailableGoToChannel.value && btnGoToChannel.value) {
            btnGoToChannel.value.focus();
        } else if (isAvailableWatchLive.value && btnWatchLive.value) {
            btnWatchLive.value.focus();
        } else if (btnAdd.value) {
            btnAdd.value.focus();
        } else if (btnSelect.value) {
            btnSelect.value.focus();
        }
    };
    const registerKeyDownListeners = () => {
        $navigator.on("keypress:down", goDown);
    };
    const  unregisterKeyDownListeners = () => {
        $navigator.off("keypress:down", goDown);
    };
    const checkParentControl = (item: PlayItem): boolean => {
        const advisory: Advisory | null | undefined = item?.advisory;
        return typeof advisory === 'number' ? !needsUnlockPin(advisory) : true;
    };
    const onListItemSelected = ()=> {
        if (!props.item) {
            throw new Error("Item is not defined");
        }
        startPlayingProgram(props.item);
        emit("play");
        if (isTypeModeEnabledPin.value) {
            saveBtnId();
        }
    };
    const playProgram = async () => {
        try {
            setPlayStatusChanging(true);
            if (isGuest.value) {
                saveBtnId();
                setTimeout(() => {
                    showPreviewSignInModal(true);
                    setPlayStatusChanging(false);
                }, 150);
                return;
            }

            if (!props.item) {
                throw new Error("Item is not defined");
            }
            
            const isAllow = checkParentControl(props.item);
            if (isAllow) {
                onListItemSelected();
            } else {
                ShowParentModal();
                SetTargetWidgetAdultControl(String(internalInstance?.uid));
            }
            setPlayStatusChanging(false);
        } catch (error) {
            Logger.captureError(error as AxiosError);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_PLAY_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
            setPlayStatusChanging(false);
        }
    };
    const saveBtnId = () => {
        let btnRef: Ref<HTMLButtonElement | null> | null = null;

        if (isPlayableItem.value) {
            btnRef = btnPlay;
        } else if (isAvailableGoToChannel.value) {
            btnRef = btnGoToChannel;
        } else if (isAvailableWatchLive.value) {
            btnRef = btnWatchLive;
        }

        if (btnRef && btnRef.value) {
            const idElement = btnRef.value.getAttribute("id");
            addSelectedIdProgram(idElement);
        }
    };
    const  showModalProgram = () => {
        if (!isDisabledChannels.value){
            showProgramModal();
        }
    };
    const  showModalSeasons = () => {
        if (!isDisabledSeasons.value){
            showSeasonModal();
        }
    };
    const toggleFavorite = async() => {
        if (isGuest.value && btnAdd.value) {
            const idElement = btnAdd.value.getAttribute("id");
            addSelectedIdProgram(idElement);
            setTimeout(() => {
                showPreviewSignInModal(true);
            }, 150);
            return;
        }

        if (!props.item?.id) {
            return;
        }

        const items: ProtoItem[] = [new ProtoItem(props.item?.id, props.item?.contentType)];
        try {
            if (isFavorite.value === false) {
                await addToFavorites(items);
            } else {
                await deleteFromFavorites(items);
            }
            await getItemProperties();
        } catch (error) {
            Logger.captureError(error as AxiosError);
        }
    };
    const goDown = () => {
        if (!isShowModalVisible.value && !isTypeModeEnabledPin.value && !isPreviewSignInModal.value) {
            emit("unset-focused");
        }
    };
    const isNotEmpty = (val: string | null): boolean => {
        if (val == null) {
            return false;
        }
        return (val != null && val.length > 0);
    };
    const playChannel = () => {
        if (!props.currentChannel) {
            throw new Error("Current channel is not defined");
        }
        void startPlayingChannel(new ChannelLiveCardData(props.currentChannel));
        if (isTypeModeEnabledPin.value) {
            saveBtnId();
        }
    };
    const playLiveProgramOfChannel = async () => {
        try {
            const time = TimeUtils.dateNow();
            setPlayStatusChanging(true);
            const program = await $services
                .guide
                .getChannelProgramByTime(Number(props.currentChannel?.id), time);
            if (!program) {
                throw new Error(`Program is not defined in getCurrentLiveProgram. Detail info: 
                    currentChannel.id: ${props.currentChannel?.id}, 
                    TimeUtils.dateNow(): ${time}, 
                    program: ${JSON.stringify(program)}`
                );
            }

            // if checked some program and adult pin is already correct and the current widget is the same as the target widget, play the channel
            if (checkedLiveProgram.value !== null && isAdultPinCorrect.value && internalInstance?.uid === Number(targetWidgetAdultControl.value)) {
                // if the program is the same or the program was updated, but the advisory is the same, play the channel
                if (checkedLiveProgram.value.id === program.id || checkedLiveProgram.value.id !== program.id && checkedLiveProgram.value.advisory === program.advisory) {
                    SetIncorrectAdultPin();
                    setCurrentTimes();
                    checkedLiveProgram.value = null;
                    playChannel();
                    setPlayStatusChanging(false);
                    return;
                }
            }

            const isAllow = checkParentControl(program);
            if (isAllow) {
                playChannel();
            } else {
                checkedLiveProgram.value = program;
                ShowParentModal();
                SetTargetWidgetAdultControl(String(internalInstance?.uid));
            }
            setPlayStatusChanging(false);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_PLAY_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
            setPlayStatusChanging(false);
        }
    };

    watch(
        isAdultPinCorrect,
        (value: boolean) => {
            if (value && internalInstance?.uid === Number(targetWidgetAdultControl.value)) {
                if (isAvailableGoToChannel.value || isAvailableWatchLive.value) {
                    void playLiveProgramOfChannel();
                    return;
                }
                SetIncorrectAdultPin();
                setCurrentTimes();
                onListItemSelected();
            }
        }
    );

    watch(
        isShowModalVisible,
        (value: boolean) => {
            if (!value) {
                focus();
            }
        }
    );
    watch(
        () => props.item,
        () => {
            void fetchBackgroundImage();
        }
    );
    watch(
        isOffline,
        (value: boolean) => {
            if (value) {
                hideSeasonModal();
                hideProgramModal();
            }
        }
    );

    defineExpose<ExposeInterface>({
        focus
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>
